import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Power, MapPin, AlertTriangle, ListChecks, ChevronLeft, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { gatesAPI, announcementZonesAPI } from '../../services/api';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';

const EMPTY_FORM = { gate_code: '', terminal: '', location: '', zone_id: '' };
const PAGE_SIZE = 15;

export default function AdminGatesPage() {
  const [gates, setGates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGate, setEditingGate] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deactivateConfirm, setDeactivateConfirm] = useState(null);
  const [activePage, setActivePage] = useState(1);
  const [inactivePage, setInactivePage] = useState(1);
  const [zones, setZones] = useState([]);

  useEffect(() => {
    loadGates();
    announcementZonesAPI.getAll().then(res => setZones(res.data.data || [])).catch(() => {});
  }, []);

  async function loadGates() {
    try {
      const res = await gatesAPI.getAll();
      setGates(res.data.data || []);
      setActivePage(1);
      setInactivePage(1);
    } catch {
      toast.error('Failed to load gates');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditingGate(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  }

  function openEdit(gate) {
    setEditingGate(gate);
    setForm({
      gate_code: gate.gate_code,
      terminal: gate.terminal || '',
      location: gate.location || '',
      zone_id: gate.zone_id != null ? String(gate.zone_id) : '',
    });
    setModalOpen(true);
  }

  async function handleSave(e) {
    e?.preventDefault();
    if (!form.gate_code.trim()) { toast.error('Gate code is required'); return; }
    try {
      setSaving(true);
      if (editingGate) {
        await gatesAPI.update(editingGate.id, form);
        toast.success('Gate updated');
      } else {
        await gatesAPI.create(form);
        toast.success('Gate created');
      }
      setModalOpen(false);
      loadGates();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save gate');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(gate) {
    // If active — deactivate (requires confirm). If inactive — reactivate directly.
    if (gate.is_active) {
      setDeactivateConfirm(gate);
    } else {
      try {
        await gatesAPI.update(gate.id, { is_active: true });
        setGates(prev => prev.map(g => g.id === gate.id ? { ...g, is_active: true } : g));
        toast.success(`Gate ${gate.gate_code} reactivated`);
      } catch {
        toast.error('Failed to reactivate gate');
      }
    }
  }

  async function confirmDeactivate() {
    if (!deactivateConfirm) return;
    try {
      await gatesAPI.deactivate(deactivateConfirm.id);
      setGates(prev => prev.map(g => g.id === deactivateConfirm.id ? { ...g, is_active: false } : g));
      toast.success(`Gate ${deactivateConfirm.gate_code} deactivated`);
    } catch {
      toast.error('Failed to deactivate gate');
    } finally {
      setDeactivateConfirm(null);
    }
  }

  const active = gates.filter(g => g.is_active);
  const inactive = gates.filter(g => !g.is_active);

  const activeTotalPages = Math.max(1, Math.ceil(active.length / PAGE_SIZE));
  const inactiveTotalPages = Math.max(1, Math.ceil(inactive.length / PAGE_SIZE));
  const activeSlice = active.slice((activePage - 1) * PAGE_SIZE, activePage * PAGE_SIZE);
  const inactiveSlice = inactive.slice((inactivePage - 1) * PAGE_SIZE, inactivePage * PAGE_SIZE);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <PageHeader
        title="Gates"
        subtitle={`${active.length} active gate${active.length !== 1 ? 's' : ''} · ${inactive.length} inactive`}
        actions={<Button variant="primary" icon={Plus} onClick={openCreate}>Add Gate</Button>}
      />

      {/* Active gates */}
      <Card padded={false} className="overflow-hidden mb-6">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Active</h2>
        </div>
        {active.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">No active gates</div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">Gate Code</th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">Terminal</th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">Location</th>
                  <th className="text-right px-5 py-3 font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeSlice.map(gate => (
                  <GateRow
                    key={gate.id}
                    gate={gate}
                    onEdit={openEdit}
                    onToggle={handleToggleActive}
                  />
                ))}
              </tbody>
            </table>
            {activeTotalPages > 1 && (
              <Pagination
                page={activePage}
                totalPages={activeTotalPages}
                total={active.length}
                pageSize={PAGE_SIZE}
                onPage={setActivePage}
              />
            )}
          </>
        )}
      </Card>

      {/* Inactive gates (collapsed by default if any) */}
      {inactive.length > 0 && (
        <Card padded={false} className="overflow-hidden opacity-70">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Inactive</h2>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {inactiveSlice.map(gate => (
                <GateRow
                  key={gate.id}
                  gate={gate}
                  onEdit={openEdit}
                  onToggle={handleToggleActive}
                />
              ))}
            </tbody>
          </table>
          {inactiveTotalPages > 1 && (
            <Pagination
              page={inactivePage}
              totalPages={inactiveTotalPages}
              total={inactive.length}
              pageSize={PAGE_SIZE}
              onPage={setInactivePage}
            />
          )}
        </Card>
      )}

      {/* ── Create / Edit Modal ─────────────────────────────────────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col max-h-[90vh]">
            <div className="px-6 py-5 border-b border-gray-100 flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-900">
                {editingGate ? `Edit Gate ${editingGate.gate_code}` : 'Add New Gate'}
              </h2>
            </div>

            <form onSubmit={handleSave} className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gate Code *</label>
                <input
                  type="text"
                  value={form.gate_code}
                  onChange={e => setForm(f => ({ ...f, gate_code: e.target.value.toUpperCase() }))}
                  placeholder="e.g. A1"
                  required
                  maxLength={10}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm uppercase font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Terminal</label>
                <input
                  type="text"
                  value={form.terminal}
                  onChange={e => setForm(f => ({ ...f, terminal: e.target.value }))}
                  placeholder="e.g. 1 or T2"
                  maxLength={10}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zone</label>
                <select
                  value={form.zone_id}
                  onChange={e => setForm(f => ({ ...f, zone_id: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">No zone (visible to all supervisors/managers)</option>
                  {zones.map(z => (
                    <option key={z.id} value={z.id}>{z.zone_name || z.zone_code}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">Scopes this gate to supervisors/managers assigned to the zone (CR 9.1)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location / Description</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                  placeholder="e.g. North Pier, Level 2"
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

            </form>

            {/* Pinned footer buttons */}
            <div className="flex gap-3 px-6 pb-5 pt-3 border-t border-gray-100 flex-shrink-0">
              <Button type="button" variant="ghost" onClick={() => setModalOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="button" variant="primary" onClick={handleSave} loading={saving} className="flex-1">
                {saving ? 'Saving…' : editingGate ? 'Save Changes' : 'Add Gate'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Deactivate Confirmation ────────────────────────────────────────── */}
      {deactivateConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={20} className="text-yellow-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Deactivate Gate {deactivateConfirm.gate_code}?</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Operators won't be able to start new sessions at this gate. Existing active sessions are not affected.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => setDeactivateConfirm(null)} className="flex-1">
                Cancel
              </Button>
              <Button variant="warning" onClick={confirmDeactivate} className="flex-1">
                Deactivate
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Pagination({ page, totalPages, total, pageSize, onPage }) {
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const pages = [];
  const delta = 2;
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - delta && i <= page + delta)) {
      pages.push(i);
    }
  }
  const withEllipsis = [];
  let prev = null;
  for (const p of pages) {
    if (prev !== null && p - prev > 1) withEllipsis.push('...');
    withEllipsis.push(p);
    prev = p;
  }

  return (
    <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50">
      <p className="text-xs text-gray-500">
        Showing {from}–{to} of {total}
      </p>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" onClick={() => onPage(page - 1)} disabled={page === 1} icon={ChevronLeft} />
        {withEllipsis.map((p, i) =>
          p === '...' ? (
            <span key={`e${i}`} className="px-1 text-xs text-gray-400">…</span>
          ) : (
            <Button
              key={p}
              variant={p === page ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => onPage(p)}
            >
              {p}
            </Button>
          )
        )}
        <Button variant="ghost" size="sm" onClick={() => onPage(page + 1)} disabled={page === totalPages} icon={ChevronRight} />
      </div>
    </div>
  );
}

function GateRow({ gate, onEdit, onToggle }) {
  const navigate = useNavigate();

  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50 transition-colors last:border-0">
      <td className="px-5 py-3">
        <span className="font-mono font-bold text-gray-900 text-base">{gate.gate_code}</span>
      </td>
      <td className="px-5 py-3 text-gray-600">
        {gate.terminal ? `T${gate.terminal}` : <span className="text-gray-300">—</span>}
      </td>
      <td className="px-5 py-3 text-gray-500 max-w-xs">
        {gate.location ? (
          <span className="flex items-center gap-1">
            <MapPin size={12} className="text-gray-400 flex-shrink-0" />
            {gate.location}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>
      <td className="px-5 py-3 text-right">
        <div className="flex items-center justify-end gap-2">
          {gate.is_active && (
            <button
              onClick={() => navigate(`/admin/gates/${gate.id}/queue`)}
              className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
              title="Manage queue"
            >
              <ListChecks size={14} />
            </button>
          )}
          <button
            onClick={() => onEdit(gate)}
            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit2 size={14} />
          </button>
          <button
            onClick={() => onToggle(gate)}
            className={`p-1.5 rounded-lg transition-colors ${
              gate.is_active
                ? 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50'
                : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
            }`}
            title={gate.is_active ? 'Deactivate' : 'Reactivate'}
          >
            <Power size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
}
